import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, where, getDoc, setDoc } from "firebase/firestore";
import { firestore, auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";

const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      if (user) {
        resolve(user);
      } else {
        reject(new Error("User not authenticated"));
      }
    });
  });
};

const ensureUserExists = async (user) => {
  try {
    const userRef = doc(firestore, "users", user.uid);
    const userSnapshot = await getDoc(userRef);

    if (!userSnapshot.exists()) {
      await setDoc(userRef, {
        fullName: user.displayName || "",
        email: user.email || "",
      });
    }
    return true;
  } catch (error) {
    return false;
  }
};

export async function addTask({ title, assignedTo, description, dueDate, priority, status = "To Do" }) {
  const user = await getCurrentUser();
  await ensureUserExists(user);
  const tasksCollection = collection(firestore, "tasks");

  const parsedDueDate = dueDate && !isNaN(new Date(dueDate).getTime())
    ? new Date(dueDate)
    : null;

  await addDoc(tasksCollection, {
    title,
    assignedTo,
    description,
    dueDate: parsedDueDate,
    priority,
    status,
    userId: user.uid,
    createdAt: serverTimestamp(),
  });
}

export const getAllTasks = async () => {
  try {
    const user = await getCurrentUser();
    await ensureUserExists(user);
    const userId = user.uid;

    const tasksCollection = collection(firestore, "tasks");
    const q = query(tasksCollection, where("userId", "==", userId));
    const tasksSnapshot = await getDocs(q);

    const allTasks = tasksSnapshot.docs.map((taskDoc) => {
      const data = taskDoc.data();
      return {
        id: taskDoc.id,
        ...data,
        dueDate: data.dueDate
          ? new Date(data.dueDate.seconds * 1000).toLocaleDateString("en-GB")
          : null,
      };
    });

    return allTasks;
  } catch (error) {
    return [];
  }
};

export const updateTask = async (taskId, updatedData) => {
  try {
    const user = await getCurrentUser();
    await ensureUserExists(user);
    const userId = user.uid;

    const taskRef = doc(firestore, "tasks", taskId);
    const taskSnapshot = await getDoc(taskRef);

    if (taskSnapshot.exists() && taskSnapshot.data().userId === userId) {
      const dataToUpdate = { ...updatedData };
      if (dataToUpdate.dueDate && typeof dataToUpdate.dueDate === 'string') {
        const parsed = new Date(dataToUpdate.dueDate);
        dataToUpdate.dueDate = isNaN(parsed.getTime()) ? null : parsed;
      }
      await updateDoc(taskRef, dataToUpdate);
    }
  } catch (error) {
    throw error;
  }
};

export const updateTaskStatus = async (taskId, status) => {
  try {
    const user = await getCurrentUser();
    await ensureUserExists(user);
    const userId = user.uid;

    const taskRef = doc(firestore, "tasks", taskId);
    const taskSnapshot = await getDoc(taskRef);

    if (taskSnapshot.exists() && taskSnapshot.data().userId === userId) {
      await updateDoc(taskRef, { status });
    }
  } catch (error) {
    throw error;
  }
};

export const deleteTask = async (taskId) => {
  try {
    const user = await getCurrentUser();
    await ensureUserExists(user);
    const userId = user.uid;

    const taskRef = doc(firestore, "tasks", taskId);
    const taskSnapshot = await getDoc(taskRef);

    if (taskSnapshot.exists() && taskSnapshot.data().userId === userId) {
      await deleteDoc(taskRef);
    }
  } catch (error) {
    throw error;
  }
};
